import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { agregarToken } from "../../features/ui/uiSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState } from "../../store";
import { useAuth } from "react-oidc-context";
import { AnimatePresence, motion } from "framer-motion";

export const BarTopComponent = () => {
  const { usuario } = useSelector((state: RootState) => state.ui);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const auth = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const menuRef = useRef<HTMLDivElement>(null);

  // Cierra el menú si se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const cerrarSesion = () => {
    dispatch(agregarToken(""));
    if (import.meta.env.VITE_SSO === "true") {
      auth.removeUser().then(() => {
        auth.signoutRedirect();
      });
    } else {
      navigate("/auth/login", { replace: true });
    }
  };

  const handleEditProfile = () => {
    setIsMenuOpen(false);
    navigate("/home/editar-perfil");
  };

  // Función para obtener las iniciales del usuario (Ej: Francisco Sanchez -> FS)
  const getInitials = (name: string) => {
    if (!name) return "US";
    const parts = name.split(" ");
    if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  // Pequeña lógica para mostrar el nombre de la ruta actual en el Breadcrumb
  const currentPathName = location.pathname.split("/").pop()?.replace(/-/g, " ") || "Inicio";

  return (
    <header className="flex h-20 flex-shrink-0 items-center justify-between border-b border-slate-200/80 bg-white px-4 md:px-8">
      {/* --- Breadcrumbs & Quick Title --- */}
      <div className="flex items-center gap-3">
        <nav className="hidden items-center space-x-2 text-sm text-slate-500 sm:flex">
          <span className="font-medium hover:text-slate-700">Insurance Services</span>
          <svg className="h-3.5 w-3.5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
          </svg>
          <span className="font-semibold capitalize text-slate-900">{currentPathName}</span>
        </nav>
        <span className="inline-flex items-center rounded-md border border-brand-100 bg-blue-50 px-2 py-0.5 text-xs font-medium text-brand-600 hidden md:flex">
          Entorno Activo
        </span>
      </div>

      {/* --- Right Header: User Profile & Corporate Emblem --- */}
      <div className="flex items-center gap-4 md:gap-6">

        {/* Notificaciones */}
        <button
          type="button"
          aria-label="Notificaciones"
          className="relative rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
          </svg>
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-brand-500 ring-2 ring-white"></span>
        </button>

        <div className="h-8 w-px bg-slate-200"></div>

        {/* User Information */}
        {usuario && (
          <div className="relative flex items-center gap-4" ref={menuRef}>
            <button
              onClick={handleProfileClick}
              className="flex items-center gap-3 text-left focus:outline-none"
            >
              <div className="hidden text-right sm:block">
                <div className="text-sm font-bold leading-snug text-slate-900">{usuario.name}</div>
                <div className="text-xs font-medium text-slate-400">{usuario.email}</div>
              </div>

              {/* Avatar (Muestra Imagen o Iniciales) */}
              {usuario.fc_avatar ? (
                <img
                  src={usuario.fc_avatar}
                  alt={usuario.name}
                  className="h-10 w-10 rounded-xl object-cover shadow-sm"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-brand-600 to-blue-400 text-sm font-semibold text-white shadow-sm">
                  {getInitials(usuario.name)}
                </div>
              )}
            </button>


            {/* Menú Desplegable Animado */}
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-14 z-[999] mt-2 w-56 origin-top-right overflow-hidden rounded-xl border border-slate-100 bg-white shadow-lg ring-1 ring-black/5"
                >
                  <div className="border-b border-slate-50 px-4 py-3 sm:hidden">
                    <p className="text-sm font-bold text-slate-900">{usuario.name}</p>
                    <p className="truncate text-xs text-slate-500">{usuario.email}</p>
                  </div>
                  <div className="p-1">
                    <button
                      onClick={handleEditProfile}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-brand-600"
                    >
                      <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                      Editar perfil
                    </button>
                    <button
                      onClick={cerrarSesion}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                    >
                      <svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path></svg>
                      Cerrar sesión
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </header>
  );
};
